import { useEffect, useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { getAppOrders, getAppOrdersForAccount, type AppOrdersResponse } from '../api/appOrders'
import { useAuthStore } from '../auth/useAuthStore'
import { useLocalStorage } from './useLocalStorage'

const CACHE_KEY = 'app-orders-cache'
const TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

interface CachedAppOrders {
  data: AppOrdersResponse
  ts: number
}

function isFresh(cached: CachedAppOrders | undefined): cached is CachedAppOrders {
  return !!cached && Date.now() - cached.ts < TTL_MS
}

export interface UseAppSymbolsResult {
  /** Broker-canonical symbols this app has placed orders on. */
  appSymbols: Set<string>
  /** Upstream broker order IDs this app has placed. */
  upstreamOrderIds: Set<string>
  /** Lookup: upstream broker order id → fleet trade group_key, for trade-detail links. */
  groupKeyByUpstream: Map<string, string>
  isLoading: boolean
  data: AppOrdersResponse | undefined
  refetch: () => void
}

export function useAppSymbols(): UseAppSymbolsResult {
  const accountId = useAuthStore((s) => s.accountId)
  const scopeAccountId = useAuthStore((s) => s.scopeAccountId)
  const accounts = useAuthStore((s) => s.accounts)
  const isAggregate = scopeAccountId === null && accounts.length > 1

  const [cached, setCached] = useLocalStorage<CachedAppOrders | undefined>(CACHE_KEY, undefined)
  const [initialSeed] = useState(() => (isFresh(cached) ? cached : undefined))

  const query = useQuery({
    queryKey: ['app-orders', accountId],
    queryFn: getAppOrders,
    enabled: !!accountId && !isAggregate,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    initialData: initialSeed?.data,
    initialDataUpdatedAt: initialSeed?.ts,
  })

  const aggregateIds = useMemo(
    () => (isAggregate ? accounts.map((a) => a.accountId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAggregate, accounts.map((a) => a.accountId).join(',')],
  )

  const aggregateQueries = useQueries({
    queries: aggregateIds.map((id) => ({
      queryKey: ['app-orders', id],
      queryFn: () => getAppOrdersForAccount(id),
      staleTime: 60_000,
      refetchOnWindowFocus: true,
    })),
  })

  const merged = useMemo<AppOrdersResponse | undefined>(() => {
    if (!isAggregate) return query.data
    if (aggregateQueries.length === 0) return { symbols: [], upstreamOrderIds: [], orderGroups: [] }
    if (aggregateQueries.some((q) => !q.data)) return undefined
    const symbols = new Set<string>()
    const ids = new Set<string>()
    const groups: Array<{ upstreamOrderId: string; groupKey: string }> = []
    const seenGroupKey = new Set<string>()
    for (const q of aggregateQueries) {
      for (const s of q.data?.symbols ?? []) symbols.add(s)
      for (const oid of q.data?.upstreamOrderIds ?? []) ids.add(oid)
      for (const g of q.data?.orderGroups ?? []) {
        if (seenGroupKey.has(g.upstreamOrderId)) continue
        seenGroupKey.add(g.upstreamOrderId)
        groups.push(g)
      }
    }
    return {
      symbols: [...symbols],
      upstreamOrderIds: [...ids],
      orderGroups: groups,
    }
  }, [
    isAggregate,
    query.data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    aggregateQueries.map((q) => q.dataUpdatedAt).join(','),
  ])

  // Persist every successful fetch so the next cold start sees up-to-date
  // symbols. The 24h TTL still applies to seeding `initialData` — older
  // entries are ignored on boot.
  useEffect(() => {
    if (!merged) return
    setCached({ data: merged, ts: Date.now() })
  }, [merged, setCached])

  const appSymbols = useMemo(() => new Set(merged?.symbols ?? []), [merged])
  const upstreamOrderIds = useMemo(() => new Set(merged?.upstreamOrderIds ?? []), [merged])
  const groupKeyByUpstream = useMemo(() => {
    const m = new Map<string, string>()
    for (const g of merged?.orderGroups ?? []) m.set(g.upstreamOrderId, g.groupKey)
    return m
  }, [merged])

  const isLoading = isAggregate
    ? aggregateQueries.length === 0 || aggregateQueries.some((q) => q.isLoading)
    : query.isLoading

  return {
    appSymbols,
    upstreamOrderIds,
    groupKeyByUpstream,
    isLoading,
    data: merged,
    refetch: () => {
      if (isAggregate) {
        for (const q of aggregateQueries) q.refetch()
      } else {
        query.refetch()
      }
    },
  }
}
